/**
 * Agencia360 — SlideOver con vista consolidada de una agencia.
 * Datos, contactos, métricas, reservas, visitas históricas.
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, CircularProgress, Chip, Paper, Stack, Divider, Button, Tooltip,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HistoryIcon from '@mui/icons-material/History';
import SlideOver from '../common/SlideOver';
import VisitaForm from './VisitaForm';
import WhatsAppButton from './WhatsAppButton';
import {
  API_BASE, getAuthHeaders, formatPrecio, formatFechaCorta, fechaRelativa,
  PIPELINE_AGENCIA, TEMPERATURAS, RESULTADOS_VISITA,
} from './format';

export default function Agencia360({ agenciaId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visitaOpen, setVisitaOpen] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    fetch(`${API_BASE}/turismo/comercial/agencia-360/${agenciaId}`,
      { headers: getAuthHeaders() })
      .then(async r => {
        if (!r.ok) {
          const e = await r.json().catch(() => ({}));
          throw new Error(e.detail || 'Error');
        }
        return r.json();
      })
      .then(d => { if (!cancel) setData(d); })
      .catch(e => { if (!cancel) setError(e.message); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [agenciaId]);

  return (
    <SlideOver open onClose={onClose} variant="wide"
      title={data?.agencia?.nombre || 'Cargando…'}
      subtitle={data?.agencia ? `${data.agencia.tipo || ''} · ${data.agencia.ciudad || ''}` : ''}
      icon={<BusinessIcon />}
      footer={
        <>
          <Button onClick={onClose} startIcon={<CloseIcon />} sx={{ textTransform: 'none' }}>Cerrar</Button>
          {data?.agencia?.telefono_general && (
            <WhatsAppButton
              numero={data.agencia.telefono_general}
              nombreContacto={data.contactos?.[0]?.nombre || data.agencia.nombre}
              empresaNombre="Entretur"
              aplicaA="agencia"
              variant="outlined"
            />
          )}
          <Button variant="contained" disableElevation startIcon={<AddIcon />}
            onClick={() => setVisitaOpen(true)}
            sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
            Registrar visita
          </Button>
        </>
      }>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress /></Box>
      ) : error ? (
        <Typography sx={{ color: '#dc2626' }}>{error}</Typography>
      ) : data && (
        <Stack spacing={2}>
          {/* Header info */}
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
              <PipelineChip estado={data.agencia.pipeline_estado} />
              <TempChip temp={data.agencia.temperatura} />
              {data.agencia.activa
                ? <Chip size="small" label="Activa" sx={{ bgcolor: '#dcfce7', color: '#166534', fontSize: 10 }} />
                : <Chip size="small" label="Inactiva" sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontSize: 10 }} />}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ fontSize: 12 }}>
              {data.agencia.telefono_general && (
                <Box><b>Tel:</b> {data.agencia.telefono_general}</Box>
              )}
              {data.agencia.correo && (<Box><b>Correo:</b> {data.agencia.correo}</Box>)}
              {data.agencia.nit_cedula && (<Box><b>NIT:</b> {data.agencia.nit_cedula}</Box>)}
            </Stack>
            {data.agencia.direccion && (
              <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.5 }}>
                <LocationOnIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.25 }} />
                {data.agencia.direccion}
              </Typography>
            )}
          </Paper>

          {/* KPIs */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
            gap: 1,
          }}>
            <Kpi label="Reservas totales" value={data.metricas.total_reservas_historico} color="#0ea5e9" />
            <Kpi label="Reservas 30d" value={data.metricas.reservas_30d} color="#0d9488" />
            <Kpi label="Reservas 90d" value={data.metricas.reservas_90d} color="#8b5cf6" />
            <Kpi label="Ingresos 30d" value={formatPrecio(data.metricas.ingresos_30d)} color="#16a34a" />
            <Kpi label="Comisiones 30d" value={formatPrecio(data.metricas.comisiones_30d)} color="#f59e0b" />
            <Kpi label="Deuda actual" value={formatPrecio(data.metricas.deuda_actual)}
              color={data.metricas.deuda_actual > 0 ? '#dc2626' : '#64748b'} />
          </Box>

          {/* Contactos */}
          {data.contactos.length > 0 && (
            <Box>
              <SectionTitle>Contactos</SectionTitle>
              <Stack spacing={0.5}>
                {data.contactos.map(c => (
                  <Paper key={c.id} variant="outlined" sx={{ p: 1, borderRadius: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{c.nombre}</Typography>
                        <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                          {c.cargo && `${c.cargo} · `}{c.numero_whatsapp}{c.correo && ` · ${c.correo}`}
                        </Typography>
                      </Box>
                      {c.numero_whatsapp && (
                        <WhatsAppButton
                          numero={c.numero_whatsapp}
                          nombreContacto={c.nombre}
                          empresaNombre="Entretur"
                          aplicaA="agencia"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}

          {/* Visitas */}
          <Box>
            <SectionTitle>Visitas recientes ({data.visitas.length})</SectionTitle>
            {data.visitas.length === 0 ? (
              <Typography sx={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
                No has registrado visitas a esta agencia aún.
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                {data.visitas.map(v => (
                  <Paper key={v.id} variant="outlined" sx={{ p: 1, borderRadius: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, flex: 1 }}>
                        {v.persona_contactada || '—'}
                        {v.cargo_persona && <span style={{ color: '#94a3b8' }}> · {v.cargo_persona}</span>}
                      </Typography>
                      <ResultadoChip resultado={v.resultado} />
                      <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>
                        {fechaRelativa(v.fecha_visita)}
                      </Typography>
                    </Stack>
                    {v.notas && (
                      <Typography sx={{ fontSize: 11.5, color: '#475569', mt: 0.5 }}>
                        {v.notas}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>

          {/* Reservas recientes */}
          <Box>
            <SectionTitle>Reservas recientes</SectionTitle>
            {data.reservas_recientes.length === 0 ? (
              <Typography sx={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
                Esta agencia aún no tiene reservas.
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                {data.reservas_recientes.slice(0, 8).map(r => (
                  <Paper key={r.id} variant="outlined" sx={{ p: 1, borderRadius: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 700 }}>
                        {r.codigo_reserva}
                      </Typography>
                      <Chip size="small" label={r.estado}
                        sx={{ fontSize: 9, height: 16, bgcolor: '#f1f5f9', color: '#475569' }} />
                      <Typography sx={{ fontSize: 11, flex: 1 }}>{r.nombre_cliente}</Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#0d9488' }}>
                        {formatPrecio(r.precio_total)}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 10.5, color: '#64748b' }}>
                      {r.paquete || '—'} · {formatFechaCorta(r.fecha_salida)}
                      {r.comision_valor > 0 && ` · comisión ${formatPrecio(r.comision_valor)}`}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>

          {data.agencia.notas_internas && (
            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: '#fef3c7' }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#92400e', mb: 0.25 }}>
                NOTAS INTERNAS
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#78350f', whiteSpace: 'pre-wrap' }}>
                {data.agencia.notas_internas}
              </Typography>
            </Paper>
          )}
        </Stack>
      )}

      {visitaOpen && (
        <VisitaForm open={visitaOpen} onClose={() => setVisitaOpen(false)}
          prefill={{ agencia_id: agenciaId, agencia_nombre: data?.agencia?.nombre }}
          onSaved={() => { setVisitaOpen(false); /* recargar mediante re-mount */ }} />
      )}
    </SlideOver>
  );
}

function PipelineChip({ estado }) {
  const meta = PIPELINE_AGENCIA.find(p => p.value === estado) || PIPELINE_AGENCIA[3];
  return <Chip size="small" label={meta.label}
    sx={{ bgcolor: `${meta.color}20`, color: meta.color, fontWeight: 700, fontSize: 10, height: 18 }} />;
}
function TempChip({ temp }) {
  const meta = TEMPERATURAS.find(t => t.value === temp) || TEMPERATURAS[1];
  return <Chip size="small" label={meta.label}
    sx={{ bgcolor: `${meta.color}15`, color: meta.color, fontWeight: 700, fontSize: 10, height: 18 }} />;
}
function ResultadoChip({ resultado }) {
  const meta = RESULTADOS_VISITA.find(r => r.value === resultado) || RESULTADOS_VISITA[0];
  return <Chip size="small" label={meta.label}
    sx={{ bgcolor: `${meta.color}15`, color: meta.color, fontWeight: 700, fontSize: 9.5, height: 16 }} />;
}
function Kpi({ label, value, color }) {
  return (
    <Paper sx={{ p: 0.75, borderRadius: 1.5, borderLeft: `3px solid ${color}` }}>
      <Typography sx={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase',
                          fontWeight: 700, letterSpacing: 0.3, lineHeight: 1.1 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 800, color, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Paper>
  );
}
function SectionTitle({ children }) {
  return (
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#0d9488',
                       textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>
      {children}
    </Typography>
  );
}
